import {useCallback, useEffect, useState} from 'react'
import {getPriceList} from 'services/priceServices'
import type {Products, PriceType, ProductType} from 'types/orderTypes'
import {AddressType} from 'types/propertyTypes'

type ProductPriceData = Products & {
  prices: PriceType[]
  product: string
}

export const useProductPrice = (address: AddressType, products: ProductType[]) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [data, setData] = useState<Required<ProductPriceData>[]>([])

  const getData = useCallback(async () => {
    setLoading(true)
    const data = await getPriceList({...address, products})
    if (data?.products) {
      setData(data.products as Required<ProductPriceData>[])
    } else {
      setError(true)
    }
    setLoading(false)
  }, [address, products])

  useEffect(() => {
    getData()
  }, [getData])

  return {data: data!, error, loading}
}

export type CDAPriceType = {
  dueDate: string
  price: number
  turnTime: number
  isMLSEnabled: boolean
}

const CDA_PRODUCT_LIST: ProductType[] = ['CDA_MLS', 'CDA']

export const useCDAProductPrice = (address: AddressType) => {
  const {data, error, loading} = useProductPrice(address, CDA_PRODUCT_LIST)
  const [cdaPriceList, setCdaPriceList] = useState<CDAPriceType[]>()
  useEffect(() => {
    setCdaPriceList(
      data.reduce((accumulator, current) => {
        if (current && current.prices) {
          current.prices.forEach(({turnTime, estimate, estimatedDueDate}) => {
            if (turnTime && estimate && estimatedDueDate) {
              accumulator.push({
                isMLSEnabled: current.product === 'CDA_MLS',
                turnTime: turnTime / 24,
                price: estimate,
                dueDate: estimatedDueDate
              })
            }
          })
        }
        return accumulator
      }, [] as CDAPriceType[])
    )
  }, [data])

  return {
    data: cdaPriceList || [],
    error,
    loading
  }
}
